/**
 * Screen Scroll Pan — „die Website lebt": ein langer Full-Page-Screenshot
 * scrollt vertikal im Geräte-Frame, als würde jemand live durch die Seite
 * scrollen. Alternativ wird ein echtes Screen-Recording (Video) abgespielt.
 *
 * - Bild (tall full-page screenshot): wird oben angesetzt und über die
 *   Clip-Dauer nach oben gepannt (`extras.scrollPct` = wie weit, in % der
 *   Bildhöhe). Dimensions-unabhängig — funktioniert mit beliebig hohen Shots.
 * - Video (.mp4/.webm/.mov): wird einfach abgespielt (echte Aufnahme).
 *
 * Inputs:
 *   - media         → 1 URL: langer Screenshot ODER Screen-Recording-Video
 *   - text          → Headline (optional)
 *   - subtext       → Caption-Pill (z. B. „www.allyver.com")
 *   - accent        → Akzent
 *   - extras.device     → "phone" | "browser" | "laptop" (Default: format-abhängig)
 *   - extras.direction  → "down" (Default) | "up"
 *   - extras.scrollPct  → wie weit gescrollt wird (Default 62, 10–92)
 */
import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import {
  accentFor, clipOutroFade, combineSceneFragments, escapeHtml, makeTl,
  typeSize, weights, withAlpha, colors as themeColors, gsapSetLine,
  pickMediaSrc, isVideoMedia,
} from "../../reel/sceneShared.js";
import { screenContent } from "./_mockHelpers.js";

function clampNum(v: unknown, def: number, min: number, max: number): number {
  const n = typeof v === "number" ? v : parseFloat(String(v ?? ""));
  if (!Number.isFinite(n)) return def;
  return Math.max(min, Math.min(max, n));
}

export const screenScrollPan: SceneBuilder = ({ ctx, clipPrefix, inputs, clipDurationSec, clipStartSec }) => {
  const accent = accentFor(inputs);
  const isVertical = ctx.format === "9:16";
  const headline = inputs.text ?? "";
  const caption = inputs.subtext ?? "";

  const deviceRaw = String(inputs.extras?.device ?? (isVertical ? "phone" : "browser"));
  const device = deviceRaw === "phone" || deviceRaw === "browser" || deviceRaw === "laptop" ? deviceRaw : (isVertical ? "phone" : "browser");
  const direction = String(inputs.extras?.direction ?? "down") === "up" ? "up" : "down";
  const scrollPct = clampNum(inputs.extras?.scrollPct, 62, 10, 92);

  const lg = typeSize("lg", ctx.format);
  const sm = typeSize("sm", ctx.format);

  const rawMedia = Array.isArray(inputs.media) ? inputs.media[0] : inputs.media;
  const src = pickMediaSrc(inputs.media);
  const isVid = isVideoMedia(rawMedia);

  // Frame-Dimensionen.
  const phoneW = isVertical ? 452 : ctx.format === "1:1" ? 410 : 372;
  const bezel = Math.round(phoneW * 0.035);
  const phoneInnerW = phoneW - bezel * 2;
  const phoneInnerH = Math.round(phoneInnerW * (2796 / 1290));
  const phoneH = phoneInnerH + bezel * 2;

  const winW = isVertical ? 900 : ctx.format === "1:1" ? 860 : 1160;
  const winH = Math.round(winW * (isVertical ? 1.34 : 0.62));
  const chromeH = Math.round(winH * 0.075);

  const t = makeTl(clipStartSec);
  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "studio", accent });

  const wrapId = `${clipPrefix}-wrap`;
  const headId = `${clipPrefix}-head`;
  const capId = `${clipPrefix}-cap`;
  const deviceId = `${clipPrefix}-device`;
  const glowId = `${clipPrefix}-glow`;
  const scrollId = `${clipPrefix}-scroll`;
  const thumbId = `${clipPrefix}-thumb`;

  // Inner: scrollendes Bild / Video / Fallback.
  let inner: string;
  if (src && isVid) {
    inner = `<video data-has-media="1" src="${escapeHtml(src)}" muted playsinline autoplay loop style="position:absolute;inset:0;width:100%;height:100%;object-fit:cover;background:#000;display:block;"></video>`;
  } else if (src) {
    inner = `<img id="${scrollId}" data-has-media="1" src="${escapeHtml(src)}" alt="screen" style="position:absolute;top:0;left:0;width:100%;height:auto;display:block;will-change:transform;" />`;
  } else {
    inner = screenContent({ media: null, accent, fallback: "feed" });
  }

  // Schmaler Scroll-Indikator (Thumb) am rechten Rand — verstärkt das „live"-Gefühl.
  const showThumb = Boolean(src) && !isVid;
  const thumbHtml = showThumb
    ? `<div style="position:absolute;top:6px;bottom:6px;right:5px;width:4px;border-radius:9999px;background:${withAlpha(themeColors.fg, 0.1)};z-index:8;">
        <div id="${thumbId}" style="position:absolute;left:0;width:100%;height:26%;border-radius:9999px;background:${withAlpha(accent, 0.8)};box-shadow:0 0 10px ${withAlpha(accent, 0.6)};"></div>
      </div>`
    : "";

  const radius = device === "phone" ? Math.round(phoneInnerW * 0.11) : device === "laptop" ? 8 : 0;
  const screenInner = `<div style="position:relative;width:100%;height:100%;overflow:hidden;background:${themeColors.bg};border-radius:${radius}px;">
    ${inner}
    ${thumbHtml}
  </div>`;

  let frameHtml: string;
  if (device === "phone") {
    frameHtml = `<div id="${deviceId}" style="position:relative;width:${phoneW}px;height:${phoneH}px;background:linear-gradient(180deg,#1a1a1c 0%,#0a0a0b 100%);border-radius:${Math.round(phoneW * 0.13)}px;padding:${bezel}px;box-shadow:0 0 90px ${withAlpha(accent, 0.35)},0 60px 130px rgba(0,0,0,0.55),inset 0 0 0 1.5px ${withAlpha(themeColors.fg, 0.08)};border:1.5px solid ${withAlpha(themeColors.fg, 0.18)};box-sizing:border-box;will-change:transform,opacity;">${screenInner}</div>`;
  } else if (device === "laptop") {
    const lh = Math.round(winW * 0.6);
    const baseW = Math.round(winW * 1.15);
    frameHtml = `<div id="${deviceId}" style="position:relative;display:flex;flex-direction:column;align-items:center;will-change:transform,opacity;">
      <div style="position:relative;width:${winW}px;height:${lh}px;background:#0e1117;border-radius:18px;padding:14px;box-shadow:0 0 80px ${withAlpha(accent, 0.3)},0 50px 110px rgba(0,0,0,0.55);border:1.5px solid ${withAlpha(themeColors.fg, 0.14)};box-sizing:border-box;">
        <div style="position:relative;width:100%;height:100%;border-radius:8px;overflow:hidden;background:${themeColors.bg};">${screenInner}</div>
      </div>
      <div style="width:${baseW}px;height:14px;background:linear-gradient(180deg,#1c2129,#0e1117);border-radius:0 0 24px 24px;box-shadow:0 16px 30px rgba(0,0,0,0.55);position:relative;"><div style="position:absolute;top:0;left:50%;transform:translateX(-50%);width:18%;height:6px;background:#000;border-radius:0 0 12px 12px;"></div></div>
    </div>`;
  } else {
    const dot = (c: string) => `<span style="width:${Math.round(chromeH * 0.24)}px;height:${Math.round(chromeH * 0.24)}px;border-radius:9999px;background:${c};display:inline-block;"></span>`;
    frameHtml = `<div id="${deviceId}" style="position:relative;width:${winW}px;height:${winH}px;background:#0e1117;border-radius:18px;box-shadow:0 0 90px ${withAlpha(accent, 0.32)},0 60px 130px rgba(0,0,0,0.55);border:1.5px solid ${withAlpha(themeColors.fg, 0.14)};overflow:hidden;box-sizing:border-box;will-change:transform,opacity;">
      <div style="height:${chromeH}px;display:flex;align-items:center;gap:10px;padding:0 ${Math.round(chromeH * 0.5)}px;background:linear-gradient(180deg,#1c2129,#11151c);border-bottom:1px solid ${withAlpha(themeColors.fg, 0.08)};">
        ${dot("#ff5f57")}${dot("#febc2e")}${dot("#28c840")}
        <div style="flex:1;margin:0 ${Math.round(chromeH * 0.4)}px;height:${Math.round(chromeH * 0.52)}px;border-radius:9999px;background:${withAlpha(themeColors.fg, 0.08)};display:flex;align-items:center;padding:0 14px;font-family:'Inter',sans-serif;font-size:${Math.round(chromeH * 0.34)}px;color:${withAlpha(themeColors.fg, 0.6)};">allyver.com</div>
      </div>
      <div style="position:relative;height:${winH - chromeH}px;">${screenInner}</div>
    </div>`;
  }

  const html = `<div id="${wrapId}" class="clip" data-start="${clipStartSec.toFixed(4)}" data-duration="${clipDurationSec.toFixed(4)}" style="position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:${isVertical ? 36 : 30}px;padding:${isVertical ? "120px 60px" : "70px"};box-sizing:border-box;">
    ${headline ? `<div id="${headId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${lg * 0.66}px;letter-spacing:-1.5px;line-height:1.04;color:${themeColors.fg};text-align:center;text-wrap:balance;max-width:${isVertical ? 820 : 1100}px;">${escapeHtml(headline)}</div>` : ""}
    <div style="position:relative;display:flex;justify-content:center;">
      <div id="${glowId}" style="position:absolute;inset:-40px;border-radius:140px;background:radial-gradient(closest-side, ${withAlpha(accent, 0.4)} 0%, transparent 70%);filter:blur(30px);"></div>
      ${frameHtml}
    </div>
    ${caption ? `<div id="${capId}" style="display:inline-flex;align-items:center;gap:10px;background:${withAlpha(themeColors.bg, 0.72)};border:1px solid ${withAlpha(accent, 0.4)};border-radius:9999px;padding:${isVertical ? 14 : 12}px ${isVertical ? 26 : 22}px;backdrop-filter:blur(8px);">
      <span style="width:9px;height:9px;border-radius:9999px;background:${accent};box-shadow:0 0 12px ${accent};"></span>
      <span style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${sm * 0.62}px;letter-spacing:0.4px;color:${themeColors.fg};">${escapeHtml(caption)}</span>
    </div>` : ""}
  </div>`;

  // ---- Timeline ----------------------------------------------------------
  const intro = 0.6;
  const tail = 0.5;
  const panDur = Math.max(1.0, clipDurationSec - intro - tail);

  const fromY = direction === "up" ? -scrollPct : 0;
  const toY = direction === "up" ? 0 : -scrollPct;
  // Thumb-Position spiegelt den Scroll-Fortschritt (0% → (100-thumbH)%).
  const thumbTravel = 100 - 26; // thumb ist 26% hoch
  const thumbFrom = direction === "up" ? thumbTravel : 0;
  const thumbTo = direction === "up" ? 0 : thumbTravel;

  const init = [
    headline ? gsapSetLine(`#${headId}`, { opacity: 0, y: 16 }) : "",
    gsapSetLine(`#${deviceId}`, { opacity: 0, y: 60, scale: 0.94, transformOrigin: "50% 60%" }),
    gsapSetLine(`#${glowId}`, { opacity: 0 }),
    caption ? gsapSetLine(`#${capId}`, { opacity: 0, y: 12 }) : "",
    src && !isVid ? gsapSetLine(`#${scrollId}`, { yPercent: fromY }) : "",
    showThumb ? gsapSetLine(`#${thumbId}`, { top: `${thumbFrom}%` }) : "",
  ].filter(Boolean).join("\n");

  const tweens: string[] = [
    t.to(`#${deviceId}`, { opacity: 1, y: 0, scale: 1, duration: 0.8, ease: "power3.out" }, 0),
    t.to(`#${glowId}`, { opacity: 0.85, duration: 0.6, ease: "power2.out" }, 0.2),
    t.to(`#${deviceId}`, { y: -8, duration: Math.max(1.0, clipDurationSec - 1.6), ease: "sine.inOut" }, 0.9),
  ];
  if (headline) tweens.push(t.to(`#${headId}`, { opacity: 1, y: 0, duration: 0.55, ease: "power2.out" }, 0.3));
  if (caption) tweens.push(t.to(`#${capId}`, { opacity: 1, y: 0, duration: 0.5, ease: "power2.out" }, 0.6));

  if (src && !isVid) {
    tweens.push(t.to(`#${scrollId}`, { yPercent: toY, duration: panDur, ease: "sine.inOut" }, intro));
    if (showThumb) tweens.push(t.to(`#${thumbId}`, { top: `${thumbTo}%`, duration: panDur, ease: "sine.inOut" }, intro));
  }

  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec, 12));

  return combineSceneFragments([backdrop, { html, init, tweens }]);
};
